import { useAppSelector, useAppDispatch } from 'src/store/store';
import {
  selectUser,
  selectIsAuthenticated,
  selectAuthLoading,
  selectAuthError,
  selectUserRoleId,
  logoutUser,
  User
} from 'src/reducers/authSlice';

/**
 * Hook to access current user and authentication state.
 *
 * Usage:
 * ```tsx
 * const { user, isAuthenticated, logout } = useAuth();
 *
 * // Access user properties
 * const userName = user?.firstName;
 * const roleId = user?.roles?.[0]?.id;
 * ```
 */
export const useAuth = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const loading = useAppSelector(selectAuthLoading);
  const error = useAppSelector(selectAuthError);
  const roleId = useAppSelector(selectUserRoleId);

  const logout = () => {
    dispatch(logoutUser());
  };

  return {
    user,
    isAuthenticated,
    loading,
    error,
    roleId,
    logout,
    // Convenience accessors
    userId: user?.userId,
    partyId: user?.partyId,
    employeeId: user?.employeeId?.id,
    EmpID: user?.employeeId?.employeeId,
    employeeUserType: user?.employeeId?.userType,
    firstName: user?.firstName,
    lastName: user?.lastName,
    fullName: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : null,
    email: user?.email,
    roles: user?.roles || [],
    roleName: user?.roles?.[0]?.name,
    // Manager/Supervisor info (from login response)
    immediateSupervisorPartyId: user?.immediateSupervisorPartyId,
    isPeopleManager: user?.jobDeatils?.peopleManager === 'Yes'
  };
};

/**
 * Type for useAuth return value
 */
export type UseAuthReturn = ReturnType<typeof useAuth>;
